import { useState } from 'react'
import { Check, Copy, Headphones, Link2, Square } from 'lucide-react'
import { motion } from 'motion/react'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'
import { EASE } from '@/components/dashboard/fx/motion'
import { TAGS, TAG_META, NEW_TAGS, formatClock } from '@/components/build/types'
import type { Entry, Frame } from '@/hooks/usePlaylistBuild'

interface RideSummaryProps {
  entries: Entry[]
  frame: Frame[]
  name: string
  minutes: number
  auditioning: boolean
  onAudition: () => void
  onStop: () => void
  onCopySetlist: () => string
  onCopyLink: () => string
}

// A row of role blocks (Warm-up, Build, ...) sized by how many songs each holds.
function Timeline({ frame }: { frame: Frame[] }) {
  const runs: { role: string; slots: number }[] = []
  frame.forEach(({ role }) => (runs.at(-1)?.role === role ? runs.at(-1)!.slots++ : runs.push({ role, slots: 1 })))

  return (
    <div>
      <div className="flex gap-1" role="img" aria-label={`Ride arc: ${runs.map((r) => r.role).join(', ')}`}>
        {runs.map((run, i) => (
          <motion.div
            key={run.role}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
            style={{ flexGrow: run.slots, backgroundColor: `color-mix(in oklab, var(--primary) ${25 + i * 15}%, transparent)` }}
            className="h-2 origin-left rounded-full"
          />
        ))}
      </div>
      <div className="mt-1.5 flex gap-1 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
        {runs.map((run) => (
          <span key={run.role} style={{ flexGrow: run.slots, flexBasis: 0 }} className="truncate">
            {run.role.split('-')[0]}
          </span>
        ))}
      </div>
    </div>
  )
}

function useCopied(action: () => string) {
  const [copied, setCopied] = useState(false)
  const run = async () => {
    try {
      await navigator.clipboard.writeText(action())
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      // Clipboard blocked (insecure context, permissions) -- nothing useful to do.
    }
  }
  return [copied, run] as const
}

export function RideSummary({ entries, frame, name, minutes, auditioning, onAudition, onStop, onCopySetlist, onCopyLink }: RideSummaryProps) {
  const items = entries.map((e) => e.item).filter((item): item is NonNullable<typeof item> => item !== null)
  const counts = Object.fromEntries(TAGS.map((tag) => [tag, items.filter((i) => i.tag === tag).length])) as Record<(typeof TAGS)[number], number>
  const fresh = NEW_TAGS.reduce((sum, tag) => sum + counts[tag], 0)
  const percentNew = items.length ? Math.round((fresh / items.length) * 100) : 0
  const artists = new Set(items.flatMap((i) => i.artists)).size
  const [copiedSetlist, copySetlist] = useCopied(onCopySetlist)
  const [copiedLink, copyLink] = useCopied(onCopyLink)
  const r = 44

  return (
    <div className="flex flex-col gap-4 lg:sticky lg:top-24">
      <SpotlightCard className="flex flex-col gap-6 p-5 sm:p-6">
        <div className="flex items-center gap-5">
          <div className="relative size-28 shrink-0">
            <svg viewBox="0 0 100 100" className="size-full -rotate-90">
              <circle cx="50" cy="50" r={r} fill="none" strokeWidth="8" className="stroke-muted" />
              <motion.circle
                cx="50"
                cy="50"
                r={r}
                fill="none"
                strokeWidth="8"
                strokeLinecap="round"
                className="stroke-[#eb6834]"
                animate={{ pathLength: percentNew / 100 }}
                initial={{ pathLength: 0 }}
                transition={{ duration: 0.9, ease: EASE }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-display text-3xl font-semibold tabular-nums tracking-tighter">{percentNew}%</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            of this ride is <span className="font-medium text-foreground">new to {name}</span> -- songs they&apos;ve never played.
          </p>
        </div>

        <div>
          <div className="flex h-3 gap-0.5 overflow-hidden rounded-full">
            {TAGS.map((tag) => (
              <motion.div
                key={tag}
                animate={{ flexGrow: counts[tag] }}
                transition={{ duration: 0.6, ease: EASE }}
                style={{ backgroundColor: TAG_META[tag].color, flexBasis: 0 }}
                className="min-w-0"
              />
            ))}
          </div>
          <ul className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
            {TAGS.map((tag) => (
              <li key={tag} className="flex items-center gap-2 text-muted-foreground">
                <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: TAG_META[tag].color }} />
                <span className="truncate">{TAG_META[tag].short}</span>
                <span className="ml-auto font-medium tabular-nums text-foreground">{counts[tag]}</span>
              </li>
            ))}
          </ul>
        </div>

        <Timeline frame={frame} />

        <dl className="grid grid-cols-3 gap-2 text-center">
          {[
            [String(items.length), 'songs'],
            [`${minutes}:00`, 'minutes'],
            [String(artists), 'artists'],
          ].map(([value, label]) => (
            <div key={label} className="rounded-xl bg-muted/60 py-2.5">
              <dt className="sr-only">{label}</dt>
              <dd className="font-display text-xl font-semibold tabular-nums">{value}</dd>
              <dd className="text-[11px] text-muted-foreground">{label}</dd>
            </div>
          ))}
        </dl>
        <p className="-mt-3 text-center text-[11px] text-muted-foreground">Runs {formatClock(frame.at(-1)?.endsAt ?? 0)} at roughly even song lengths</p>
      </SpotlightCard>

      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={auditioning ? onStop : onAudition}
          disabled={items.length === 0}
          className="flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3.5 font-medium text-primary-foreground shadow-lg shadow-primary/25 transition-transform hover:scale-[1.02] disabled:opacity-50"
        >
          {auditioning ? <Square className="size-4 fill-current" /> : <Headphones className="size-4" />}
          {auditioning ? 'Stop the audition' : 'Audition the whole ride'}
        </button>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={copySetlist} className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card/60 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted">
            {copiedSetlist ? <Check className="size-4 text-primary" /> : <Copy className="size-4" />}
            {copiedSetlist ? 'Copied' : 'Copy setlist'}
          </button>
          <button type="button" onClick={copyLink} className="flex items-center justify-center gap-2 rounded-2xl border border-border bg-card/60 px-4 py-3 text-sm font-medium transition-colors hover:bg-muted">
            {copiedLink ? <Check className="size-4 text-primary" /> : <Link2 className="size-4" />}
            {copiedLink ? 'Copied' : 'Copy link'}
          </button>
        </div>
      </div>
    </div>
  )
}
